import { IconLoader2 } from '@tabler/icons-react'

import {
  Avatar as ShadcnAvatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

interface AvatarProps {
  name?: string
  src?: string | null
  className?: string
  fallbackClassName?: string
  shape?: 'circle' | 'square'
  isLoading?: boolean
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/)

  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase()
  }

  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

function getColorFromInitials(initials: string): string {
  // Using darker shades (600-700) for better contrast with white text
  const colors = [
    'bg-blue-600',
    'bg-green-600',
    'bg-purple-600',
    'bg-orange-600',
    'bg-pink-600',
    'bg-indigo-600',
    'bg-teal-600',
    'bg-cyan-600',
    'bg-rose-600',
    'bg-amber-600',
  ]

  let hash = 0
  for (let i = 0; i < initials.length; i++) {
    hash = initials.charCodeAt(i) + ((hash << 5) - hash)
  }

  return colors[Math.abs(hash) % colors.length]
}

export function Avatar({
  name,
  src,
  className,
  fallbackClassName,
  shape = 'circle',
  isLoading = false,
}: AvatarProps) {
  const displayName = name ?? '?'
  const initials = name ? getInitials(name) : '?'
  const bgColorClass = name ? getColorFromInitials(initials) : 'bg-muted'
  const textColorClass = name ? '!text-white' : 'text-muted-foreground'
  const normalizedSrc = src || null
  const avatarKey = normalizedSrc ?? 'fallback'

  const shapeClass = shape === 'square' ? '!rounded-md after:!rounded-md' : ''
  const fallbackShapeClass = shape === 'square' ? '!rounded-md' : ''
  const imageShapeClass = shape === 'square' ? '!rounded-md' : ''

  return (
    <span className="relative inline-block leading-none">
      {/* Atomic inline: an ancestor's underline stops here. The wrapper
          cannot do it, a flex item is blockified. */}
      <ShadcnAvatar
        key={avatarKey}
        className={cn('inline-flex align-top', shapeClass, className)}
      >
        {normalizedSrc && (
          <AvatarImage
            src={normalizedSrc}
            alt={displayName}
            className={imageShapeClass}
          />
        )}
        <AvatarFallback
          className={cn(
            bgColorClass,
            textColorClass,
            fallbackShapeClass,
            fallbackClassName,
          )}
        >
          {initials}
        </AvatarFallback>
      </ShadcnAvatar>
      {isLoading && (
        <span
          className={cn(
            'absolute inset-0 flex items-center justify-center bg-black/50',
            shape === 'square' ? 'rounded-md' : 'rounded-full',
          )}
        >
          <IconLoader2 className="animate-spin text-white" />
        </span>
      )}
    </span>
  )
}
